import { useState } from 'react'
import { config } from 'data/config'
import Hero from 'assets/images/partners/travel-partner-hero.png'
import Header from 'assets/images/partners/travel-partner-header.png'
import ImageOne from 'assets/images/partners/travel-partner-1.png'
import ImageTwo from 'assets/images/partners/travel-partner-2.png'
import LayeredInformationSection from 'components/sections/LayeredInformationSection'
import InformationSection from 'components/sections/InformationSection'
import PublicLayout, { PublicLayoutProps } from 'layouts/PublicLayout'
import PartnerSearchbar from 'components/searchbar/PartnerSearchbar'
import ImageHeader from 'components/ImageHeader'
import TravelAgentApplySection from 'components/sections/partner-pages/TravelAgentApplySection'
import TravelAgentInquiryModal from 'components/TravelAgentInquiryModal'
import { useMobileApp } from 'data/hooks/MobileApp/useMobileApp'
import { useHasScrolled } from 'data/hooks/useHasScrolled'

const TravelPartnersPage = () => {
  const [formModalOpen, setFormModalOpen] = useState(false)
  const { isMobileApp } = useMobileApp()
  const hasScrolled = useHasScrolled(0, true)

  const seo: PublicLayoutProps['seo'] = {
    title: 'Contact A.M.A Selections for Luxury Holiday Villa Rentals in Spain, France & Italy',
    description: 'Contact A.M.A Selections at any time to find out more about availability & exact rates of our luxury holiday villas for rent in France, Spain & Italy.',
    openGraph: {
      type: 'business',
      url: config.appURL + '/contact-us',
      images: [
        {
          url: Hero.src,
          width: 1922,
          height: 760,
        },
      ],
    },
  }

  return (
    <PublicLayout
      seo={seo}
      mobileNavBar={{
        visible: isMobileApp || hasScrolled,
        back: true,
        profileTrigger: true,
        links: [
          {
            label: 'Owners',
            href: '/owners',
          },
          {
            label: 'Travel Partners',
            href: '/travel-partners',
            active: true,
          },
        ],
      }}
    >
      <PartnerSearchbar currentPage="travel-partners" />

      <ImageHeader
        title={'A.M.A Selections for\nTravel Advisors & Concierge Companies'}
        image={Hero}
        altText="View of a beautiful coastline"
      />

      <InformationSection
        title="Join us as a valued travel partner"
        subtitle="Travel Partners"
        text={[
          'A.M.A Selections is a leading booking platform offering a curated selection of the world’s most exceptional holiday homes. We offer over 2000 professionally managed properties, each handpicked by our team for their character, amenities and comfort.',
          'Our portfolio is just the starting point for creating lasting memories for your guests. Through our meticulous quality control and commitment to service, we strive to exceed every expectation. Our dedicated team will work with you to get the details just right for your guests before, during, and after each stay.',
          'A.M.A Selections is proud to partner with the finest luxury travel agents and concierge companies to provide world-class getaways. We offer commission to all of our travel partners and have streamlined the booking process, providing a best-in-class B2B experience and white-labeled solution. Apply today to join us as a valued travel partner.',
        ]}
        image={{
          url: Header,
          altText: 'View of sunbeds on a luxury sandy beach',
        }}
        buttonText="Apply Now"
        buttonAction={() => setFormModalOpen(true)}
      />

      <LayeredInformationSection
        header="Unlock an elevated partnership experience"
        image={{
          url: ImageOne,
          alt: 'View of an outdoor dining area',
        }}
        imageAlignment="right"
        content={[
          {
            header: 'An Exceptional, Curated Portfolio',
            content: 'Gain access to a portfolio of 2000+ luxury vacation rentals, all professionally managed. Each property has undergone rigorous vetting and passed our strict quality control standards. Our local partners conduct regular site inspections to ensure that the properties are maintained year after year, allowing them to remain in our ever-growing portfolio.',
          },
          {
            header: 'Streamlined Support and Assistance',
            content: 'Upon request, our villa specialists can assist you in finding the properties that best match your client’s criteria. Once a booking is confirmed, our dedicated concierge team will work with you to help craft an exceptional getaway for your clients, from beginning to end.',
          },
          {
            header: 'Best-in-Class White-Labeled Solution',
            content: 'We understand that you know your clients best and believe in making your life easier. That’s why we’ve simplified the process for you. For each request, our villa specialists will send you a curated selection of properties via white-labeled links, complete with live availability and pricing, for you to share with your clients. You can also book on their behalf and stay up-to-date with all the details through your dedicated dashboard and the shared trip itinerary.',
          },
        ]}
      />

      <LayeredInformationSection
        image={{
          url: ImageTwo,
          alt: 'An open air massage table surrounded by greenery',
        }}
        imageAlignment="left"
        content={
          [{
            header: 'Expand Your Offering',
            content: 'There is no better time than now to immerse your clients in the villa experience. Leverage this opportunity to broaden your clients’ travel horizons in a rapidly-growing luxury market. For every booking you bring, we split our commission, often resulting in a 10% commission for you. Your commission will remain confidential and will be deposited into your account following each payment received from the guest.',
          },
          {
            header: 'Loyalty Credit on Bookings',
            content: 'In addition to the commission earned on bookings, our trusted travel partners also earn loyalty credit, which can be applied towards future bookings. You have the flexibility to either apply your loyalty credit as a discount for your next booking or opt to receive it as additional commission.',
          }]
        }
      />

      <TravelAgentApplySection onClick={() => setFormModalOpen(true)} />

      <TravelAgentInquiryModal isOpen={formModalOpen} onClose={() => setFormModalOpen(false)} />
    </PublicLayout>
  )
}

export default TravelPartnersPage
